import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import type { RolloutDayKey, RolloutKind } from 'fym-shared/src/music24/releaseRollout';
import { useAddRolloutDraftsMutation } from '../../../../../redux/api/fymAPI';
import { AddRolloutDraftsResponse } from '../../../../../redux/api/fymAPI.types';
import { SocialPlatform } from '../../types';
import { StudioActionFeedback, studioActionErrorFeedback } from '../../studio/studioActionErrorText';

export interface AddReleaseDraftsInput {
  kind: RolloutKind;
  platforms: SocialPlatform[];
  /** Only these days (one gap, or the ticked push weeks). Absent: every day. */
  dayKeys?: RolloutDayKey[];
  /** A missing kit asset of a day becomes a Studio job first (`assetQueued`). */
  makeMissing?: boolean;
}

export interface AddReleaseDrafts {
  /** Adds the drafts. Null when the call failed (the error is in `error`) or a 402 opened the packs. */
  add: (input: AddReleaseDraftsInput) => Promise<AddRolloutDraftsResponse | null>;
  isAdding: boolean;
  /** The sentence of the last failed add, for the line next to the button. Null after a success. */
  error: StudioActionFeedback | null;
  /** The answer of the last add that worked. */
  result: AddRolloutDraftsResponse | null;
}

/**
 * "Add N drafts" and "Add a draft" of a release (designs f1, f2, b1 Planned). It keeps the last
 * answer and the last error, so each screen shows them next to its button (owner rule
 * 2026-10-06: an action error sits next to its button, never in a toast). A 402 opened the credit
 * packs already (`customBaseQuery`), so it shows no error line.
 */
export function useAddReleaseDrafts({
  accountId,
  catalogItemUuid,
}: {
  accountId: number;
  catalogItemUuid: string;
}): AddReleaseDrafts {
  const intl = useIntl();
  const [addRolloutDrafts, { isLoading }] = useAddRolloutDraftsMutation();
  const [error, setError] = useState<StudioActionFeedback | null>(null);
  const [result, setResult] = useState<AddRolloutDraftsResponse | null>(null);

  const add = useCallback(
    async (input: AddReleaseDraftsInput): Promise<AddRolloutDraftsResponse | null> => {
      setError(null);
      try {
        const answer = await addRolloutDrafts({ accountId, catalogItemUuid, ...input }).unwrap();
        setResult(answer);
        return answer;
      } catch (caught: unknown) {
        if (caught instanceof FYMAPIError && caught.statusCode === 402) {
          return null;
        }
        setError(studioActionErrorFeedback(intl, 'addReleaseDrafts', caught));
        return null;
      }
    },
    [accountId, addRolloutDrafts, catalogItemUuid, intl]
  );

  return { add, isAdding: isLoading, error, result };
}
